import { isEmpty } from 'lodash'
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { quoteId } from '../cache'
import { PaymentType, UsageQuery, useUsageQuery } from '../generated/graphql'
import { updateQuoteMutation } from '../hooks'
import { useQuote } from '../hooks/useQuote'
import { ProviderContext } from '../Provider'
import { handleQuoteProgress } from '../utils/handleQuoteProgress'
import { usePreQuoteContext } from './PageValidator'

type CoverActions = {
  setInceptionDate?: { inceptionDate: string }
  setPaymentType?: { paymentType: PaymentType }
  setVoluntaryExcess?: { voluntaryExcess: number }
  setCurrentRenewalPrice?: { current: number }
  setPolicyUsage?: { requestedUsage: string }
}

export interface CoverPageContextType {
  validateAndSave: (value?: string) => boolean
  setCoverActions: (value: CoverActions) => void
  coverActions: CoverActions
  usageData: UsageQuery | undefined
}

export const defaultCoverContext = {
  setCoverActions: () => {},
  validateAndSave: () => false,
  coverActions: {},
  usageData: undefined,
}

export const CoverContext = createContext<CoverPageContextType>(defaultCoverContext)

interface CarPageProviderProps {
  children: ReactNode
}

const CoverPageProvider = ({ children }: CarPageProviderProps) => {
  const history = useHistory()
  const quote = useQuote()
  const { updateQuote } = updateQuoteMutation()
  const { setIsFetching, redirectCheckout } = useContext(ProviderContext)
  const { validateTransition } = usePreQuoteContext()

  const spouseUsageRelationshipCodes = ['S', 'J', 'W']
  const spouseUsageAvailable = !!quote?.details.additional_drivers?.find(driver =>
    spouseUsageRelationshipCodes.includes(driver?.relationship || ''),
  )
  const { data: usageData } = useUsageQuery({ variables: { spouse: spouseUsageAvailable } })

  useEffect(() => {
    setIsFetching(!usageData)
  }, [usageData])

  const [coverActions, setCoverActions] = useState<CoverActions>({})
  const id = quoteId() || ''

  useEffect(() => {
    const target = window.location.pathname
    const status = quote?.status?.sections
    const progress = handleQuoteProgress(status, target)
    if (target !== progress && progress) {
      history.push(progress)
    }
  }, [])

  const handleSaveCoverDetails = async (valid: boolean, step?: string) => {
    if (!isEmpty(coverActions)) {
      setIsFetching(true)
      await updateQuote({
        variables: {
          id,
          actions: [
            {
              ...coverActions,
            },
          ],
        },
      })
      setIsFetching(false)
    }
    if (step === 'driver' || step === 'car') {
      history.push(`/details/${step}`)
      return
    }
    if (valid) {
      redirectCheckout('quote')
    }
  }

  const validateAndSave = (step?: string) => {
    let valid = true
    if (step === 'quote' || !step) {
      valid = validateTransition()
    }
    handleSaveCoverDetails(valid, step)
    return valid
  }

  return (
    <CoverContext.Provider
      value={{
        validateAndSave,
        coverActions,
        setCoverActions,
        usageData,
      }}
    >
      {children}
    </CoverContext.Provider>
  )
}

export const useCoverPageContext = () => useContext(CoverContext)

export default CoverPageProvider
